/**
 * AutoPlay/디버그 대시보드용 게임 상태 스냅샷 빌더 (일감 S0) — Phaser-free.
 * GameScene getAutoPlaySnapshot에서 추출: 컨트롤러는 ctx, 전투 결정은 CombatSystem,
 * 씬 런타임 값(activeTab/모달/벽돌 수/던전 모드)은 getter 주입.
 * autoplay/ 디렉토리가 아닌 app/에 두는 이유: DebugDashboardModal 등 dev 공용 소비처가 있어
 * "autoplay import = dev-only" 규약을 깨지 않기 위함.
 */
import { EconomyController } from "../systems/EconomyController.js";
import { estimateDps } from "../systems/combatStats.js";
import type { CombatSystem } from "../systems/CombatSystem.js";
import { findPath } from "../modules/ModulePathFinder.js";
import { buildInventoryRows } from "../ui/tabData.js";
import { formatSpecSummary } from "../ui/specSummary.js";
import type { DailyShopTabData } from "../ui/DailyShopTabView.js";
import type { TabId } from "../ui/tabVisibility.js";
import { RELIC_DEFS_ORDERED } from "../data/balance.js";
import {
  DAILY_RESET_KEY_DUNGEON_TICKETS,
  DAILY_RESET_KEY_MYSTERY,
} from "../models/ModuleBrickSaveData.js";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { GameContext } from "./GameContext.js";

export interface GuideQuestPanelSnapshot {
  readonly visible: boolean;
  readonly updateCount: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly action: string;
  readonly title: string;
  readonly bgDepth?: number;
  readonly titleDepth?: number;
  readonly buttonDepth?: number;
  readonly bgAlpha?: number;
  readonly bgFillAlpha?: number;
  readonly titleVisible?: boolean;
  readonly titleAlpha?: number;
  readonly titleDisplayIndex?: number;
  readonly objectsAtCenter?: readonly string[];
}

export interface AutoPlaySnapshot {
  readonly gold: number;
  readonly diamond: number;
  readonly stone: number;
  readonly chapter: number;
  readonly bestChapter: number;
  readonly baseDamage: number;
  readonly dps: number;
  readonly rebirthPoint: number;
  readonly rebirthCount: number;
  readonly activeTab: TabId;
  readonly settingsOpen: boolean;
  readonly activeBricks: number;
  readonly goldBoxCost: number;
  /** 골드 상자 10연 가격 (현재 단가 × 할인 배수). */
  readonly goldBox10Cost: number;
  readonly diamondBoxCost: number;
  readonly canRebirth: boolean;
  readonly canBuyUpgrade: boolean;
  readonly unlockedCount: number;
  readonly nextUnlockCost: number;
  readonly pathConnected: boolean;
  /** 발동(active) 모듈 정의 id — S→F로 물이 실제 통과하는 경로 위 모듈. */
  readonly pathDefinitionIds: string[];
  /** 물은 흐르지만 발동 안 하는 모듈 instance id (막다른 곁가지 + 미연결 흐름). QA 검증용. */
  readonly pathWaterOnlyInstanceIds: string[];
  /** 물이 닿지 않는 배치 모듈 instance id (잘못 놓인 파이프 — 포트 틀림·고립). QA 검증용. */
  readonly pathBlockedInstanceIds: string[];
  /** 실제 전투 스펙 기준 발동 중인 시너지 id. UI 배지와 전투 효과의 판정 일치 QA용. */
  readonly activeSynergyIds: readonly string[];
  readonly placed: ReadonlyArray<{
    x: number;
    y: number;
    instanceId: string;
    definitionId: string;
    shapeId: string;
    footprintId: string;
  }>;
  readonly specSummary: string;
  readonly instances: ReadonlyArray<{
    instanceId: string;
    definitionId: string;
    name: string;
    level: number;
    rarity: string;
    placed: boolean;
    footprintId: string;
  }>;
  readonly ownedRelicCount: number;
  /** effectType별 보유 유물 합산 % (dev QA — 효과 반영 확인). */
  readonly relicBonuses: {
    readonly attackPercent: number;
    readonly goldGainPercent: number;
    readonly fireRatePercent: number;
    readonly projectileSpeedPercent: number;
  };
  readonly relics: ReadonlyArray<{
    readonly relicId: string;
    readonly grade: string;
    readonly effectType: string;
    readonly count: number;
    readonly level: number;
    readonly effectValue: number;
    readonly cost: number;
  }>;
  /** 던전 (dev QA). index/clearedMax는 0-based 내부값. */
  readonly dungeonIndex: number;
  readonly dungeonClearedMax: number;
  readonly dungeonTickets: number;
  readonly dungeonTicketLimit: number;
  readonly dungeonMsUntilReset: number;
  readonly dungeonCanChallenge: boolean;
  readonly dungeonCanSweep: boolean;
  readonly dungeonMode: boolean;
  /** 일일상점 탭 (dev QA) — 슬롯/리롤/카운트다운. */
  readonly dailyShop: DailyShopTabData;
  /** 미스터리(?) 블록 일일 보상 카운터 (dev QA — 다이아·유물 일일 제한 검증). */
  readonly mysteryDiamondRemaining: number;
  readonly mysteryRelicRemaining: number;
  readonly mysteryDiamondGrantedToday: number;
  readonly mysteryRelicGrantedToday: number;
  readonly mysteryMsUntilReset: number;
  /** 튜토리얼 진행 상태 (dev QA — START/RESET_TUTORIAL E2E 검증용). */
  readonly tutorialActive: boolean;
  /** 현재 튜토리얼 스텝 id (비활성 시 ""). */
  readonly tutorialStepId: string;
  /** 현재 가이드퀘스트 id. 모든 가이드퀘스트 수령 완료 시 "". */
  readonly guideQuestId: string;
  readonly guideQuestCompleted: boolean;
  readonly guideQuestProgress: number;
  readonly guideQuestTarget: number;
  readonly guideQuestPanel: GuideQuestPanelSnapshot;
}

export interface AutoPlaySnapshotDeps {
  ctx: GameContext;
  combat: CombatSystem;
  dailyReset: () => DailyResetService;
  activeTab: () => TabId;
  settingsOpen: () => boolean;
  activeBricks: () => number;
  dungeonMode: () => boolean;
  dailyShopData: () => DailyShopTabData;
  tutorialActive: () => boolean;
  tutorialStepId: () => string;
  guideQuestPanel: () => GuideQuestPanelSnapshot;
}

export function buildAutoPlaySnapshot(deps: AutoPlaySnapshotDeps): AutoPlaySnapshot {
  const { ctx, combat } = deps;
  const grid = ctx.grid();
  const economy = ctx.economy();
  const chapter = ctx.chapter();
  const rebirth = ctx.rebirth();
  const relic = ctx.relic();
  const dungeon = ctx.dungeon();
  const mystery = ctx.mysteryBlock();
  const inventory = ctx.inventory();
  const guideQuest = ctx.guideQuest().active();
  const dungeonMode = deps.dungeonMode();
  const path = findPath(grid);
  const state = grid.toState();
  const combatSpec = combat.buildCombatSpec();
  return {
    gold: economy.gold.toNumber(),
    diamond: economy.diamond.toNumber(),
    stone: ctx.upgradeInv().universalUpgradeStone,
    chapter: chapter.currentChapter,
    bestChapter: chapter.bestChapter,
    baseDamage: combat.baseDamage().toNumber(),
    dps: estimateDps(combatSpec.spec, combat.fireIntervalMs()).toNumber(),
    rebirthPoint: rebirth.rebirthPoint,
    rebirthCount: rebirth.rebirthCount,
    activeTab: deps.activeTab(),
    settingsOpen: deps.settingsOpen(),
    activeBricks: deps.activeBricks(),
    goldBoxCost: EconomyController.goldBoxCost(ctx.goldBox().boxOpenCount).toNumber(),
    goldBox10Cost: EconomyController.goldBox10Cost(ctx.goldBox().boxOpenCount).toNumber(),
    diamondBoxCost: EconomyController.diamondBoxCost().toNumber(),
    canRebirth: rebirth.canRebirth(chapter.currentChapter),
    canBuyUpgrade: rebirth.rebirthPoint >= 1,
    unlockedCount: state.unlockedCells.length,
    nextUnlockCost: grid.unlockCost(),
    pathConnected: path.connected,
    pathDefinitionIds: path.activeDefinitionIds,
    pathWaterOnlyInstanceIds: path.waterOnlyInstanceIds,
    pathBlockedInstanceIds: path.blockedInstanceIds,
    activeSynergyIds: combatSpec.matchedSynergyIds,
    placed: grid.placed().map((m) => ({
      x: m.x,
      y: m.y,
      instanceId: m.instanceId,
      definitionId: m.definitionId,
      shapeId: m.shapeId,
      footprintId: m.footprintId,
    })),
    specSummary: formatSpecSummary(combatSpec.spec),
    instances: buildInventoryRows(
      inventory,
      grid,
      ctx.moduleRegistry(),
      ctx.upgradeInv(),
      (id) => ctx.playerModuleLevels().level(id),
    ).map((r) => ({
      instanceId: r.instanceId,
      definitionId: r.definitionId,
      name: r.name,
      level: r.level,
      rarity: r.rarity,
      placed: r.placed,
      footprintId: inventory.getInstance(r.instanceId)?.footprintId ?? "single",
    })),
    ownedRelicCount: relic.ownedKindCount,
    relicBonuses: {
      attackPercent: relic.sumPercent("attackPercent"),
      goldGainPercent: relic.sumPercent("goldGainPercent"),
      fireRatePercent: relic.sumPercent("fireRatePercent"),
      projectileSpeedPercent: relic.sumPercent("projectileSpeedPercent"),
    },
    relics: RELIC_DEFS_ORDERED.map((d) => ({
      relicId: d.relicId,
      grade: d.grade,
      effectType: d.effectType,
      count: relic.count(d.relicId),
      level: relic.level(d.relicId),
      effectValue: Math.round(relic.effectValueAt(d.relicId)),
      cost: d.cost,
    })),
    dungeonIndex: dungeon.currentIndex,
    dungeonClearedMax: dungeon.clearedMaxIndex,
    dungeonTickets: dungeon.ticketsRemaining,
    dungeonTicketLimit: dungeon.ticketLimit,
    dungeonMsUntilReset: deps.dailyReset().msUntilNextReset(
      DAILY_RESET_KEY_DUNGEON_TICKETS,
    ),
    dungeonCanChallenge: !dungeonMode && dungeon.canChallenge(),
    dungeonCanSweep: !dungeonMode && dungeon.canSweep(),
    dungeonMode,
    dailyShop: deps.dailyShopData(),
    mysteryDiamondRemaining: mystery.diamondRemaining,
    mysteryRelicRemaining: mystery.relicRemaining,
    mysteryDiamondGrantedToday: mystery.diamondGrantedToday,
    mysteryRelicGrantedToday: mystery.relicGrantedToday,
    mysteryMsUntilReset: deps.dailyReset().msUntilNextReset(DAILY_RESET_KEY_MYSTERY),
    tutorialActive: deps.tutorialActive(),
    tutorialStepId: deps.tutorialStepId(),
    guideQuestId: guideQuest?.definition.questId ?? "",
    guideQuestCompleted: guideQuest?.completed ?? true,
    guideQuestProgress: guideQuest?.progress ?? 0,
    guideQuestTarget: guideQuest?.targetCount ?? 0,
    guideQuestPanel: deps.guideQuestPanel(),
  };
}
